import { Brain, ChevronLeft, ChevronRight, Search, ShieldAlert, X } from "lucide-react";
import * as React from "react";

import { Trans, useLingui } from "@lingui/react/macro";

import { Badge, Button, cn } from "@ando/ui";

import type { HypermemFactSummary } from "@ando/core";

import { useHypermemFacts } from "@/features/admin/hooks/useAdminHypermem";
import { formatInt, formatTimestamp } from "@/features/admin/lib/adminFormat";

import { HmEmpty, HmError, HmLoading } from "./HmStates";

/**
 * HyperMem facts browser tab (v2 port, Jun 6, 2026) — HIGH PII SENSITIVITY.
 *
 * Privacy-first browser for episodic / semantic facts extracted from user
 * conversations. The fact text is PII: redaction / inspect-mode gating is
 * enforced server-side (the BE truncates and masks unless an audited reveal
 * is active), so the FE simply renders what the endpoint returns and surfaces
 * a prominent banner reminding the operator that the access is logged. The FE
 * never re-implements redaction client-side — that is the BE's job and a
 * DevTools admin could trivially bypass any client-side masking.
 *
 * Ports the v1 ``FactsBrowserTab`` core: pillar switch (episodic / semantic),
 * optional ``user_id`` filter, limit/offset pagination driven by ``total``,
 * confidence / salience display, active / inactive badge, and fact text.
 */

const PAGE_SIZE = 50;

type Pillar = "episodic" | "semantic";

const PILLARS: ReadonlyArray<{ id: Pillar; label: string }> = [
  { id: "episodic", label: "Episodic" },
  { id: "semantic", label: "Semantic" },
];

function FactRow({ fact }: { fact: HypermemFactSummary }): React.ReactElement {
  return (
    <li className="grid grid-cols-[7rem_1fr_4.5rem_4.5rem_8rem_5rem] items-start gap-3 px-3 py-2.5 text-sm">
      <code className="truncate text-ink-2 text-xs" title={fact.user_id}>
        {fact.user_id}
      </code>
      <p className="whitespace-pre-wrap break-words text-ink-1">{fact.fact_text || "—"}</p>
      <span className="text-right text-ink-2 text-xs tabular-nums">
        {Number.isFinite(fact.confidence) ? fact.confidence.toFixed(2) : "—"}
      </span>
      <span className="text-right text-ink-2 text-xs tabular-nums">
        {typeof fact.salience_score === "number" ? fact.salience_score.toFixed(2) : "—"}
      </span>
      <span className="text-ink-3 text-xs tabular-nums">{formatTimestamp(fact.created_at)}</span>
      <span className="flex justify-end">
        {fact.is_active ? (
          <Badge variant="success" className="text-xs">
            <Trans>Attivo</Trans>
          </Badge>
        ) : (
          <Badge variant="secondary" className="text-xs">
            <Trans>Inattivo</Trans>
          </Badge>
        )}
      </span>
    </li>
  );
}

export function HmFactsBrowserTab({
  tenantId,
}: {
  tenantId: string;
}): React.ReactElement {
  const { t } = useLingui();
  const [pillar, setPillar] = React.useState<Pillar>("episodic");
  const [userIdInput, setUserIdInput] = React.useState("");
  const [appliedUserId, setAppliedUserId] = React.useState("");
  const [page, setPage] = React.useState(0);

  const opts = React.useMemo(
    () => ({
      user_id: appliedUserId.trim() || undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [appliedUserId, page],
  );

  const { data, isLoading, isError, refetch } = useHypermemFacts(pillar, tenantId, opts);

  const facts = data?.facts ?? [];
  const total = data?.total ?? 0;
  const hasPrev = page > 0;
  const hasNext = (page + 1) * PAGE_SIZE < total;
  const rangeStart = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const rangeEnd = page * PAGE_SIZE + facts.length;

  const applyFilter = React.useCallback(() => {
    setAppliedUserId(userIdInput);
    setPage(0);
  }, [userIdInput]);

  const clearFilter = React.useCallback(() => {
    setUserIdInput("");
    setAppliedUserId("");
    setPage(0);
  }, []);

  return (
    <div className="space-y-4">
      {/* PII banner — access to fact text is server-redacted and logged. */}
      <div className="flex items-start gap-3 rounded-xl border border-amber-300/70 bg-transparent p-4 text-amber-900 text-sm">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <div className="space-y-0.5">
          <p className="font-semibold">
            <Trans>Dati personali (PII) — accesso registrato</Trans>
          </p>
          <p className="text-amber-800">
            <Trans>
              I fact contengono dati estratti dalle conversazioni utente. Il testo è redatto e
              mascherato lato server salvo modalità Inspect (audit log accessibile all'utente, Art.
              15 GDPR).
            </Trans>
          </p>
        </div>
      </div>

      {/* Toolbar: pillar switch + user_id filter */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1.5" role="group" aria-label={t`Pillar`}>
          {PILLARS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => {
                setPillar(p.id);
                setPage(0);
              }}
              aria-pressed={pillar === p.id}
              className={cn(
                "rounded-lg px-3 py-1.5 font-medium text-sm transition-colors",
                pillar === p.id
                  ? "bg-ando-orange/15 text-ando-orange"
                  : "text-ink-2 hover:bg-ink-3/10",
              )}
            >
              {p.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <label className="sr-only" htmlFor="hm-facts-userid">
            <Trans>Filtra per User ID</Trans>
          </label>
          <div className="relative">
            <Search
              className="-translate-y-1/2 pointer-events-none absolute top-1/2 left-2.5 h-3.5 w-3.5 text-ink-3"
              aria-hidden
            />
            <input
              id="hm-facts-userid"
              type="text"
              value={userIdInput}
              onChange={(e) => setUserIdInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") applyFilter();
              }}
              placeholder={t`User ID (opzionale)`}
              className="h-8 w-64 rounded-lg border border-hairline bg-cream/40 pr-2 pl-8 text-ink-1 text-sm placeholder:text-ink-3 focus:border-ando-orange/50 focus:outline-none"
            />
          </div>
          <Button variant="secondary" size="sm" onClick={applyFilter}>
            <Trans>Filtra</Trans>
          </Button>
          {appliedUserId ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={clearFilter}
              aria-label={t`Rimuovi filtro User ID`}
            >
              <X aria-hidden />
            </Button>
          ) : null}
        </div>
      </div>

      {/* States */}
      {isLoading && <HmLoading label={<Trans>Caricamento fact…</Trans>} />}

      {isError && !isLoading && (
        <HmError message={<Trans>Impossibile caricare i fact.</Trans>} onRetry={() => refetch()} />
      )}

      {!isLoading && !isError && facts.length === 0 && (
        <HmEmpty
          icon={<Brain className="h-8 w-8 opacity-50" aria-hidden />}
          label={<Trans>Nessun fact per questi filtri.</Trans>}
        />
      )}

      {!isLoading && !isError && facts.length > 0 && (
        <section className="overflow-hidden rounded-xl border border-hairline bg-cream/40">
          <div className="grid grid-cols-[7rem_1fr_4.5rem_4.5rem_8rem_5rem] gap-3 border-hairline border-b px-3 py-2 font-semibold text-ink-3 text-xs">
            <span>
              <Trans>User</Trans>
            </span>
            <span>
              <Trans>Fact text</Trans>
            </span>
            <span className="text-right">
              <Trans>Conf.</Trans>
            </span>
            <span className="text-right">
              <Trans>Salience</Trans>
            </span>
            <span>
              <Trans>Creato</Trans>
            </span>
            <span className="text-right">
              <Trans>Stato</Trans>
            </span>
          </div>
          <ul className="divide-y divide-hairline">
            {facts.map((f) => (
              <FactRow key={f.fact_id} fact={f} />
            ))}
          </ul>

          {/* Pagination */}
          <div className="flex items-center justify-between gap-3 border-hairline border-t px-3 py-2.5 text-ink-3 text-xs">
            <span className="tabular-nums">
              <Trans>
                {rangeStart}–{rangeEnd} di {formatInt(total)}
              </Trans>
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={!hasPrev}
              >
                <ChevronLeft aria-hidden />
                <Trans>Precedente</Trans>
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage((p) => p + 1)}
                disabled={!hasNext}
              >
                <Trans>Successiva</Trans>
                <ChevronRight aria-hidden />
              </Button>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
